'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { ReactNode, useRef, useState } from 'react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { AutoplayOptions } from 'swiper/types';
import { MaterialIcon } from '../MaterialIcon';
// import 'swiper/swiper-bundle.css'; // moved to global cms-bundle.css

type CarouselProps = {
  children: ReactNode[];
  navigation?: {
    showOnDesktop?: boolean;
    showOnMobile?: boolean;
    nextElClassName?: string;
    nextElement?: ReactNode;
    prevElClassName?: string;
    prevElement?: ReactNode;
    disabledClassName?: string;
  };
  pagination?: {
    showOnMobile?: boolean;
    showOnDesktop?: boolean;
  };
  autoplay?: boolean | AutoplayOptions;
  fullscreenOnMobile?: boolean;
  slideClassName?: string;
  beforeInitSlideClassName?: string;
  slidesPerView?: number | 'auto';
  slidesPerViewBreakpoints?: [number, number][];
  spaceBetween?: number;
  spaceBetweenBreakpoints?: [number, number][];
  /**
   * Receives the Swiper instance on init. Needed by consumers that must drive
   * autoplay imperatively — e.g. pausing while something inside the carousel
   * holds keyboard focus, which Swiper has no parameter for.
   */
  onSwiper?: SwiperProps['onSwiper'];
  /**
   * Wrap around at the ends instead of dead-ending. Without it, navigation marks
   * prev disabled on the first slide and next on the last, while `autoplay`
   * keeps cycling (its `stopOnLastSlide` defaults to false) — so the arrows and
   * the auto-scroll disagree. Unlike `loop` this needs no slide cloning.
   */
  rewind?: boolean;
};

export function FeCarouselV2({
  children,
  autoplay,
  navigation,
  pagination,
  fullscreenOnMobile,
  slideClassName,
  beforeInitSlideClassName,
  slidesPerView = 'auto',
  slidesPerViewBreakpoints,
  spaceBetween,
  spaceBetweenBreakpoints,
  onSwiper,
  rewind,
}: CarouselProps): JSX.Element {
  const [isInitialized, setIsInitialized] = useState(false);
  const prevElRef = useRef<HTMLDivElement>(null);
  const nextElRef = useRef<HTMLDivElement>(null);
  const paginationElRef = useRef<HTMLDivElement>(null);
  const hasSwiperSpacing =
    spaceBetween !== undefined || Boolean(spaceBetweenBreakpoints?.length);

  const swiperProps: SwiperProps = {
    centeredSlides: false,
    loop: false,
    rewind,
    modules: [Navigation, Pagination, Autoplay],
    autoplay: autoplay,
    slidesPerView: slidesPerView,
    spaceBetween,
    observer: true,
    observeParents: true,
  };

  if (slidesPerViewBreakpoints?.length || spaceBetweenBreakpoints?.length) {
    swiperProps.breakpoints = {};
    for (const [slidesPerView, breakpoint] of slidesPerViewBreakpoints ?? []) {
      swiperProps.breakpoints[breakpoint] = { slidesPerView };
    }
    for (const [spaceBetween, breakpoint] of spaceBetweenBreakpoints ?? []) {
      swiperProps.breakpoints[breakpoint] = {
        ...swiperProps.breakpoints[breakpoint],
        spaceBetween,
      };
    }
  }

  // initialize navigation
  if (navigation?.showOnDesktop || navigation?.showOnMobile) {
    swiperProps.navigation = {
      prevEl: prevElRef.current,
      nextEl: nextElRef.current,
      disabledClass: classNames(
        'cursor-default text-text/disabled',
        navigation?.disabledClassName,
      ),
    };
  }

  // Use an external element for pagination so dots render below the carousel
  if (pagination?.showOnMobile || pagination?.showOnDesktop) {
    swiperProps.pagination = {
      el: paginationElRef.current,
      clickable: true,
    };
  }

  const defaultNavigationClassNames = classNames(
    'absolute top-1/2 z-[2] -translate-y-1/2 cursor-pointer text-black',
    {
      hidden: !navigation?.showOnDesktop && !navigation?.showOnMobile,
    },
    {
      'lg:block': navigation?.showOnDesktop && !navigation?.showOnMobile,
    },
    {
      'block lg:hidden': !navigation?.showOnDesktop && navigation?.showOnMobile,
    },
    { block: navigation?.showOnDesktop && navigation?.showOnMobile },
    // Keep the (non-functional) arrows hidden until Swiper initialises, so they
    // don't flash during SSR/hydration before Swiper decides scrollability and
    // wires the click handlers. Refs stay mounted (display:none) so Swiper can
    // still bind navigation on init.
    { '!hidden': !isInitialized },
  );

  return (
    <div className={classNames('relative transition')}>
      <div
        className={classNames(
          defaultNavigationClassNames,
          '-left-6',
          navigation?.prevElClassName,
        )}
        ref={prevElRef}
      >
        {/* block - render as block to remove extra inline line-height space; keeps the container at 24px high */}
        {navigation?.prevElement ?? (
          <MaterialIcon
            icon="expand_less"
            px={24}
            className="block -rotate-90"
          />
        )}
      </div>
      <div className={classNames('flex h-full grow items-center')}>
        <Swiper
          {...swiperProps}
          className={classNames('size-full h-fit', {
            'overflow-visible lg:overflow-hidden': fullscreenOnMobile,
          })}
          onBeforeInit={(swiper) => {
            if (
              (navigation?.showOnDesktop || navigation?.showOnMobile) &&
              typeof swiper.params.navigation !== 'boolean' &&
              prevElRef.current !== null &&
              nextElRef.current !== null
            ) {
              swiper.params.navigation!.prevEl = prevElRef.current;
              swiper.params.navigation!.nextEl = nextElRef.current;
            }

            if (
              (pagination?.showOnMobile || pagination?.showOnDesktop) &&
              paginationElRef.current !== null &&
              typeof swiper.params.pagination !== 'boolean'
            ) {
              swiper.params.pagination!.el = paginationElRef.current;
            }
          }}
          onSwiper={onSwiper}
          onAfterInit={() => {
            setIsInitialized(true);
          }}
        >
          {(children ?? []).map((child, index) => (
            <SwiperSlide
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={classNames(
                {
                  'mr-spacing/gap/soft-8px': !hasSwiperSpacing,
                  [beforeInitSlideClassName ?? '']: !isInitialized,
                },
                'w-auto',
                slideClassName,
              )}
            >
              {child}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {/* External pagination container rendered under the Swiper */}
      {(pagination?.showOnMobile || pagination?.showOnDesktop) && (
        <div
          className={classNames('mt-spacing/padding/soft-12px', {
            'lg:hidden': pagination?.showOnMobile && !pagination?.showOnDesktop,
            'lg:block': pagination?.showOnDesktop,
          })}
        >
          <div
            ref={paginationElRef}
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className={classNames(
              'swiper-pagination relative !bottom-0  leading-[6px]',
              'opacity-50',
              '[--swiper-pagination-bullet-size:6px]',
              '[--swiper-pagination-bullet-inactive-opacity:0.2]',
              '[--swiper-pagination-bullet-opacity:1]',
              '[--swiper-pagination-bullet-inactive-color:--text-primary]',
              '[--swiper-pagination-color:--text-primary]',
              '[--swiper-pagination-bullet-horizontal-gap:calc(var(--spacing-gap-soft-8px)/2)]',
            )}
          />
        </div>
      )}
      <div
        className={classNames(
          defaultNavigationClassNames,
          '-right-6',
          navigation?.nextElClassName,
        )}
        ref={nextElRef}
      >
        {navigation?.nextElement ?? (
          <MaterialIcon
            icon="expand_less"
            px={24}
            className="block rotate-90"
          />
        )}
      </div>
    </div>
  );
}
